import axios, {AxiosError, AxiosInstance} from 'axios'
import {IAuthData} from '../types/user'
import {CookiesService} from './Cookie.service'
import {detectaCodigoMalicioso} from '../utils/sanitize'

export interface FirstLoginError {
    status?: number
    detail?: string
}

export class AuthApi {
    private cookiesService: CookiesService
    private api: AxiosInstance

    constructor() {
        this.cookiesService = new CookiesService()
        this.api = axios.create({
            baseURL: `${import.meta.env.VITE_BASE_URL}/login`,
            headers: {
                Authorization: `Bearer ${import.meta.env.VITE_SECRET}`,
            },
        })
    }

    async login(username: string, password: string, scope = ''): Promise<IAuthData> {
        try {
            if (
                detectaCodigoMalicioso(username) ||
                detectaCodigoMalicioso(password)
            ) {
                throw new Error('Acesso negado')
            }
            const res: {data: IAuthData} = await this.api.post(
                '/',
                {
                    username,
                    password,
                    scope,
                },
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            )
            this.cookiesService.saveAuthData(res.data)
            this.cookiesService.saveIsAdmin(scope === 'admin', res.data.access_token)
            return res.data
        } catch (error: any) {
            console.log('Login error: ', error)
            if (error.response?.status === 403) {
                throw 403
            } else if (error.message === 'Network Error' || !error.response) {
                throw 'Servidor indisponível. Tente novamente mais tarde.'
            } else {
                throw 'Usuário ou senha incorretos.'
            }
        }
    }

    async sendEmailVerification(email: string) {
        try {
            const response = await this.api.get(
                `/send_email_confirmation/${email}`
            )
            return
        } catch (error) {
            console.log(error)
            throw 'Não foi possível enviar a confirmação de email. Tente novamente.'
        }
    }
    async sendEmailForResetPassword(email: string) {
        try {
            const response = await this.api.get(`/reset_password/${email}`)
            return
        } catch (error) {
            console.log(error)
            throw 'Não foi possível enviar o link para redefinição de senha. Tente novamente.'
        }
    }
    async sendTokenToResetPassword(token: string, password: string) {
        try {
            const response = await this.api.put(
                `/reset_password/${token}`,
                {
                    username: 'teste',
                    password,
                },
                {
                    headers: {
                        'content-type': 'application/x-www-form-urlencoded',
                    },
                }
            )
            return
        } catch (error) {
            console.log(error)
            throw 'Não foi possível redefinir sua senha. Tente novamente.'
        }
    }

    async firstLoginSetPassword(token: string, password: string) {
        try {
            await this.api.post(`/first_login/${token}`, {
                password: password,
                password_confirmation: password,
            })
            return
        } catch (error) {
            const err = error as AxiosError<{detail?: unknown}>
            const detail = err.response?.data?.detail
            throw {
                status: err.response?.status,
                detail: typeof detail === 'string' ? detail : undefined,
            } as FirstLoginError
        }
    }

    async signUp(
        first_name: string,
        last_name: string,
        email: string,
        user_data_phone: string,
        organization_name: string,
        password: string
    ): Promise<void> {
        try {
            if (
                detectaCodigoMalicioso(first_name) ||
                detectaCodigoMalicioso(last_name) ||
                detectaCodigoMalicioso(email) ||
                detectaCodigoMalicioso(user_data_phone) ||
                detectaCodigoMalicioso(organization_name) ||
                detectaCodigoMalicioso(password)
            ) {
                throw new Error('Acesso negado.')
            }
            user_data_phone = this.formatPhoneNumber(user_data_phone)
            const res: {
                data: {
                    created_at: Date
                    guid: string
                    first_name: string
                    last_name: string
                    email: string
                    user_data_phone: string
                    organization: string
                    last_session: Date
                    modified_at: Date
                    is_active: boolean
                }
            } = await this.api.post(
                '/create',
                {
                    first_name,
                    last_name,
                    email,
                    user_data_phone,
                    organization_name,
                    password,
                    user_data_agreed_to_terms: true,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            await this.login(email, password)
        } catch (error: any) {
            console.log('SignUp Error: ', error)
            if (error === 403) {
                throw error
            } else {
                throw 'Erro ao cadastrar novo usuário.'
            }
        }
    }

    async confirmEmail(token: string) {
        try {
            const res: {data: IAuthData} = await this.api.get(
                `/confirm_email/${token}`
            )
            this.cookiesService.saveAuthData(res.data)
            return res.data
        } catch (error: any) {
            console.log('erro ----->', error.data.detail)
            console.log('erro ----->', error.detail)
            throw 'Erro ao confirmar email. Tente novamente'
        }
    }

    async confirmInvite(token: string) {
        try {
            const res: {data: IAuthData} = await this.api.get(
                `/login/invite/accept/${token}`
            )
            this.cookiesService.saveAuthData(res.data)
            return res.data
        } catch (error: any) {
            console.log('erro ----->', error.data.detail)
            console.log('erro ----->', error.detail)
            throw 'Erro ao confirmar email. Tente novamente'
        }
    }

    formatPhoneNumber = (phone: string): string => {
        const onlyNumbers = phone.replace(/\D/g, '')

        if (onlyNumbers.startsWith('55')) {
            return `+${onlyNumbers}`
        }

        return `+55${onlyNumbers}`
    }

    editUser(
        firstName: string,
        lastName: string,
        phone: string,
        address: string,
        code: string
    ) {
        alert('Em breve')
    }

    editEmail(email: string) {
        alert('Em breve')
    }

    async doRefreshToken(): Promise<IAuthData> {
        try {
            const refreshToken =
                this.cookiesService.getAuthData()?.refresh_token

            if (!refreshToken) {
                this.cookiesService.removeAuthData()
                this.cookiesService.removeAssistant()
                this.cookiesService.removeIsAdmin()
                throw (window.location.href = '/login')
            }
            const response = await this.api.get(`/refresh/${refreshToken}`)

            const newAuthData: IAuthData = response.data
            this.cookiesService.saveAuthData(newAuthData)

            return newAuthData
        } catch (error) {
            this.cookiesService.removeAuthData()
            this.cookiesService.removeAssistant()
            this.cookiesService.removeIsAdmin()
            throw (window.location.href = '/login')
        }
    }
}
